import type {
  ExecutionCargoHydration,
  ExecutionProgressHydration,
  ExecutionStopHydration
} from '../../executionOperations/types';
import { isExecutionProjectionConsumersMode } from './executionProjectionConsumerFlags';
import type {
  EffectiveExecutionView,
  ExecutionCargoProjection,
  RouteExecutionProjection
} from './routeExecutionProjectionTypes';

function cargoFromProjection(row: ExecutionCargoProjection): ExecutionCargoHydration {
  return {
    cargoId: row.cargoId,
    fromExecutionStopUid: row.fromExecutionStopUid,
    toExecutionStopUid: row.toExecutionStopUid,
    executionState: row.currentState,
    plannedQty: row.plannedQty,
    loadedQty: row.loadedQty,
    unloadedQty: row.unloadedQty,
    remainingQty: row.remainingQty,
    isDelivered: row.isDelivered,
    isPartiallyDelivered: row.isPartiallyDelivered,
    loadedAt: null,
    unloadedAt: null
  };
}

function mergeStopsFromProjection(
  hydration: ExecutionProgressHydration,
  projection: RouteExecutionProjection
): ExecutionStopHydration[] {
  const byUid = new Map(projection.stopProgress.map((s) => [s.executionStopUid, s]));
  return hydration.stops.map((stop) => {
    const p = byUid.get(stop.executionStopUid);
    if (!p) return stop;
    return {
      ...stop,
      executionState: p.executionState,
      completionPercent: p.completionPercent,
      isCompleted: p.isCompleted
    };
  });
}

export type EffectiveExecutionOptions = {
  /** E2.1 operational cockpit — projection-only surface. */
  cockpit?: boolean;
};

/** Prefer authoritative projection fields when consumer/cockpit mode is on. */
export function buildEffectiveExecutionView(
  hydration: ExecutionProgressHydration | null | undefined,
  options?: EffectiveExecutionOptions
): EffectiveExecutionView | null {
  if (!hydration) return null;

  const projection = hydration.routeExecutionProjection ?? null;
  const usesProjection = Boolean(
    projection && (options?.cockpit || isExecutionProjectionConsumersMode())
  );

  if (!usesProjection || !projection) {
    return {
      stops: hydration.stops,
      cargo: hydration.cargo,
      progress: hydration.progress,
      financeReadiness: hydration.financeReadiness,
      projection,
      usesProjection: false
    };
  }

  const cargo = projection.cargoProgress.map(cargoFromProjection);
  const progress = projection.progress;
  const financeReadiness =
    hydration.projectionCompletionReadiness?.financeReadiness ?? hydration.financeReadiness;

  return {
    stops: mergeStopsFromProjection(hydration, projection),
    cargo,
    progress,
    financeReadiness,
    projection,
    usesProjection: true
  };
}

export function getProjectionBlockers(
  hydration: ExecutionProgressHydration | null | undefined
): string[] {
  const view = buildEffectiveExecutionView(hydration);
  if (!view) return [];
  return view.progress.blockers ?? [];
}

export function getVehicleLoadSummary(
  hydration: ExecutionProgressHydration | null | undefined
): RouteExecutionProjection['vehicleLoad'] | null {
  const view = buildEffectiveExecutionView(hydration);
  return view?.projection?.vehicleLoad ?? null;
}

export function getPartialUnloadProjection(
  hydration: ExecutionProgressHydration | null | undefined
): RouteExecutionProjection['partialExecution'] | null {
  const view = buildEffectiveExecutionView(hydration);
  return view?.projection?.partialExecution ?? null;
}

export function getExecutionTimelineFromProjection(
  hydration: ExecutionProgressHydration | null | undefined
): RouteExecutionProjection['executionTimeline'] {
  const view = buildEffectiveExecutionView(hydration);
  return view?.projection?.executionTimeline ?? [];
}
